import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  forwardRef,
  inject,
  Injector,
  input,
  output,
  Signal,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, NgControl, ReactiveFormsModule,} from '@angular/forms';
import {DateTime} from 'luxon';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

import {DatePickerModule} from 'primeng/datepicker';
import {FloatLabelModule} from 'primeng/floatlabel';
import {IftaLabelModule} from 'primeng/iftalabel';
import {IconFieldModule} from 'primeng/iconfield';
import {InputIconModule} from 'primeng/inputicon';
import {ValidationErrorsDictionary} from '../../../../core/dictionary/validation-errors.dictionary';

export type LabelMode = 'float' | 'ifta' | 'static' | 'none';
export type DateInputSize = 'small' | 'large';
export type DateSelectionMode = 'single' | 'multiple' | 'range';
export type DateValueType = 'luxon' | 'date' | 'iso' | 'timestamp';
export type DateView = 'date' | 'month' | 'year';
export type IconPosition = 'left' | 'right';

export interface DateInputIcon {
  icon: string;
  position: IconPosition;
  clickable?: boolean;
}

@Component({
  selector: 'app-date-input',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DatePickerModule,
    FloatLabelModule,
    IftaLabelModule,
    IconFieldModule,
    InputIconModule,
  ],
  templateUrl: './date-input.component.html',
  styleUrl: './date-input.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppDateInputComponent),
      multi: true,
    }
  ],
})
export class AppDateInputComponent implements ControlValueAccessor, AfterViewInit {
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private ngControl: NgControl | null = null;

  readonly inputId = input<string>(`date-input-${crypto.randomUUID().slice(0, 8)}`);
  readonly label = input<string>('');
  readonly placeholder = input<string>('');
  readonly labelMode = input<LabelMode>('float');
  readonly size = input<DateInputSize | undefined>(undefined);
  readonly fluid = input<boolean>(true);
  readonly helpText = input<string>('');
  readonly filled = input<boolean>(false);
  readonly disabled = input<boolean>(false);
  readonly readonly = input<boolean>(false);
  readonly selectionMode = input<DateSelectionMode>('single');
  readonly valueType = input<DateValueType>('luxon');
  readonly dateFormat = input<string>('dd/MM/yyyy');
  readonly minDate = input<DateTime | Date | null>(null);
  readonly maxDate = input<DateTime | Date | null>(null);
  readonly fromNow = input<boolean>(false);
  readonly untilNow = input<boolean>(false);
  readonly disabledDates = input<Date[]>([]);
  readonly disabledDays = input<number[]>([]);
  readonly showTime = input<boolean>(false);
  readonly timeOnly = input<boolean>(false);
  readonly hourFormat = input<'12' | '24'>('24');
  readonly showSeconds = input<boolean>(false);
  readonly showButtonBar = input<boolean>(false);
  readonly showWeek = input<boolean>(false);
  readonly view = input<DateView>('date');
  readonly showIcon = input<boolean>(true);
  readonly iconDisplay = input<'input' | 'button'>('input');
  readonly icon = input<DateInputIcon | null>(null);
  readonly inline = input<boolean>(false);
  readonly numberOfMonths = input<number>(1);
  readonly locale = input<string>('pl');
  readonly firstDayOfWeek = input<number>(1);
  readonly showErrors = input<boolean>(true);
  readonly name = input<string>('');
  readonly appendTo = input<string | null>('body');
  readonly showClear = input<boolean>(false);
  readonly autofocus = input<boolean>(false);
  readonly showOnFocus = input<boolean>(false);
  readonly dateDisplayFormat = input<string>('dd.mm.yy');

  readonly focused = output<Event>();
  readonly blurred = output<Event>();
  readonly iconClicked = output<void>();
  readonly valueChanged = output<DateTime | DateTime[] | Date | Date[] | string | string[] | number | number[] | null>();
  readonly calendarShown = output<void>();
  readonly calendarHidden = output<void>();
  readonly monthChanged = output<{ month?: number; year?: number }>();
  readonly yearChanged = output<{ month?: number; year?: number }>();

  readonly value = signal<Date | Date[] | null>(null);
  readonly isFocused = signal<boolean>(false);
  readonly isTouched = signal<boolean>(false);
  readonly isDisabled = signal<boolean>(false);
  readonly controlState = signal<{
    invalid: boolean;
    touched: boolean;
    dirty: boolean;
    errors: any;
  }>({
    invalid: false,
    touched: false,
    dirty: false,
    errors: null,
  });

  readonly computedDisabled = computed(() =>
    this.disabled() || this.isDisabled()
  );

  readonly hasRequiredRule: Signal<boolean> = computed(() => {
    const state = this.controlState();
    return state.errors?.['required'] !== undefined;
  });

  readonly isInvalid = computed(() => {
    const state = this.controlState();
    return state.invalid && (state.touched || state.dirty);
  });

  readonly errorMessage = computed(() => {
    const state = this.controlState();
    if (!state.errors) return null;
    return ValidationErrorsDictionary.resolveValidationErrors(state.errors);
  });

  readonly hasLabel = computed(() =>
    !!this.label() && this.labelMode() !== 'none'
  );

  readonly hasCustomIcon = computed(() => !!this.icon());

  readonly computedMinDate = computed(() => {
    if (!this.fromNow()) {
      const min = this.minDate();
      if (!min) return undefined;

      return min instanceof Date ? min : min.toJSDate();
    }

    return this.now;
  });

  readonly computedMaxDate = computed(() => {
    const max = this.maxDate();

    if (max) {
      return max instanceof Date ? max : max.toJSDate();
    }

    if (this.untilNow()) {
      return this.now;
    }

    return undefined;
  });

  private onChange: (value: DateTime | DateTime[] | Date | Date[] | string | string[] | number | number[] | null) => void = () => {
  };
  private onTouched: () => void = () => {
  };

  private updateControlState(): void {
    if (this.ngControl?.control) {
      const ctrl = this.ngControl.control;
      this.controlState.set({
        invalid: ctrl.invalid,
        touched: ctrl.touched,
        dirty: ctrl.dirty,
        errors: ctrl.errors,
      });
    }
  }

  writeValue(value: DateTime | DateTime[] | Date | Date[] | string | string[] | number | number[] | null): void {
    const nativeValue = this.inputToNative(value);
    this.value.set(nativeValue);
  }

  registerOnChange(fn: (value: DateTime | DateTime[] | Date | Date[] | string | string[] | number | number[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  ngAfterViewInit(): void {
    this.ngControl = this.injector.get(NgControl, null, {
      optional: true,
      self: true,
    });

    if (this.ngControl?.control) {
      const ctrl = this.ngControl.control;

      ctrl.statusChanges?.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.updateControlState();
        });

      this.updateControlState();
    }
  }

  protected onFocus(event: Event): void {
    this.isFocused.set(true);
    this.focused.emit(event);
  }

  protected onBlur(event: Event): void {
    this.isFocused.set(false);
    this.isTouched.set(true);
    this.onTouched();
    this.blurred.emit(event);
    this.updateControlState();
  }

  protected onIconClick(): void {
    if (this.icon()?.clickable) {
      this.iconClicked.emit();
    }
  }

  protected onSelect(event: Date | Date[] | any): void {
    const selectedDate = event instanceof Date || Array.isArray(event) ? event : event?.value;

    if (selectedDate) {
      this.value.set(selectedDate);
      const outputValue = this.nativeToOutputType(selectedDate);
      this.onChange(outputValue);
      this.valueChanged.emit(outputValue);
    }
  }

  protected onClear(): void {
    this.value.set(null);
    this.onChange(null);
    this.valueChanged.emit(null);
  }

  protected onInput(event: any): void {
    const inputValue = event.target?.value;
    if (inputValue) {
      const date = event as Date;
      this.value.set(date);
      const outputValue = this.nativeToOutputType(date);
      this.onChange(outputValue);
      this.valueChanged.emit(outputValue);
    }
  }

  protected onShow(): void {
    this.calendarShown.emit();
  }

  protected onHide(): void {
    this.calendarHidden.emit();
  }

  protected onMonthChange(event: { month?: number; year?: number }): void {
    this.monthChanged.emit(event);
  }

  protected onYearChange(event: { month?: number; year?: number }): void {
    this.yearChanged.emit(event);
  }

  protected onClickOutside(): void {
  }

  private inputToNative(value: DateTime | DateTime[] | Date | Date[] | string | string[] | number | number[] | null): Date | Date[] | null {
    if (value === null || value === undefined) return null;

    if (Array.isArray(value)) {
      return value
        .map(v => this.singleInputToNative(v))
        .filter((v): v is Date => v !== null);
    }

    return this.singleInputToNative(value);
  }

  private singleInputToNative(value: DateTime | Date | string | number): Date | null {
    if (value instanceof Date) return value;
    if (value instanceof DateTime) return value.toJSDate();
    if (typeof value === 'string') {
      const dt = DateTime.fromISO(value);
      return dt.isValid ? dt.toJSDate() : null;
    }
    if (typeof value === 'number') {
      const dt = DateTime.fromMillis(value);
      return dt.isValid ? dt.toJSDate() : null;
    }
    return null;
  }

  private nativeToOutputType(value: Date | Date[] | null): DateTime | DateTime[] | Date | Date[] | string | string[] | number | number[] | null {
    if (value === null) return null;

    const type = this.valueType();

    if (Array.isArray(value)) {
      switch (type) {
        case 'luxon':
          return value.map(d => DateTime.fromJSDate(d));
        case 'date':
          return value;
        case 'iso':
          return value.map(d => DateTime.fromJSDate(d).toISO() ?? '');
        case 'timestamp':
          return value.map(d => DateTime.fromJSDate(d).toMillis());
      }
    }

    switch (type) {
      case 'luxon':
        return DateTime.fromJSDate(value);
      case 'date':
        return value;
      case 'iso':
        return DateTime.fromJSDate(value).toISO();
      case 'timestamp':
        return DateTime.fromJSDate(value).toMillis();
    }
  }

  markAsTouched(): void {
    this.isTouched.set(true);
    this.onTouched();
    this.updateControlState();
  }

  reset(): void {
    this.isTouched.set(false);
    this.isFocused.set(false);
    this.value.set(null);
    this.onChange(null);
  }

  getLuxonValue(): DateTime | DateTime[] | null {
    const nativeValue = this.value();
    if (nativeValue === null) return null;

    if (Array.isArray(nativeValue)) {
      return nativeValue.map(d => DateTime.fromJSDate(d));
    }

    return DateTime.fromJSDate(nativeValue);
  }

  setLuxonValue(value: DateTime | DateTime[] | null): void {
    const nativeValue = this.luxonToNative(value);
    this.value.set(nativeValue);
    const outputValue = this.nativeToOutputType(nativeValue);
    this.onChange(outputValue);
    this.valueChanged.emit(outputValue);
  }

  getNativeValue(): Date | Date[] | null {
    return this.value();
  }

  setNativeValue(value: Date | Date[] | null): void {
    this.value.set(value);
    const outputValue = this.nativeToOutputType(value);
    this.onChange(outputValue);
    this.valueChanged.emit(outputValue);
  }

  private luxonToNative(value: DateTime | DateTime[] | null): Date | Date[] | null {
    if (value === null) return null;

    if (Array.isArray(value)) {
      return value.map(dt => dt.toJSDate());
    }

    return value.toJSDate();
  }

  get now(): Date {
    return DateTime.now().toJSDate()
  }
}
